///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sentences for the earthquake cutout HUD, derived from the redux cutout
/// state. Pure so the HUD stays a thin renderer.
///

import type { NodalPlaneState, QuakeCutoutAnalysis, QuakeCutoutExposure, QuakeCutoutHistory, QuakeCutoutSlab, QuakeCutoutState, QuakeCutoutSummary } from "@/redux/dialogSlice"

export function pad3(deg: number): string {
	return String(Math.round(((deg % 360) + 360) % 360)).padStart(3, "0")
}

function npText(n: NodalPlaneState): string {
	return `${pad3(n.strikeDeg)}°/${Math.round(n.dipDeg)}°`
}

/** What the selected quake is in its cluster, and what surrounds it */
export function sequenceSentence(cutout: QuakeCutoutState): string | undefined {
	const s = cutout.summary
	const q = cutout.quake
	if (!s || !q) return undefined
	if (cutout.role === "member") {
		const largest = cutout.clusterLargest
		const parts = [`Part of ongoing activity: ${s.sequenceCount} events in the past 30 days`]
		if (largest) parts.push(`largest M ${largest.mag.toFixed(1)}`)
		parts.push(`this one is M ${q.mag.toFixed(1)}`)
		return parts.join(" · ")
	}
	const others = s.sequenceCount - 1
	const parts: string[] = []
	parts.push(others === 0 ? "No other events in this sequence yet" : `${others} related event${others === 1 ? "" : "s"}`)
	if (s.foreshockCount > 0) parts.push(`${s.foreshockCount} foreshock${s.foreshockCount === 1 ? "" : "s"}`)
	if (s.largestAftershockMag !== undefined) parts.push(`largest aftershock M ${s.largestAftershockMag.toFixed(1)}`)
	return parts.join(" · ")
}

/** Block dimensions and the printed scales */
export function blockSentence(s: QuakeCutoutSummary): string {
	const parts = [`${s.lengthKm} km along strike ${pad3(s.strikeDeg)}°`, `${s.depthFloorKm} km deep`]
	if (s.depthScale > 1) parts.push(`depth ×${s.depthScale}`)
	if (s.terrainExaggeration) parts.push(`terrain ×${s.terrainExaggeration}`)
	if (s.seaFloorExaggeration) parts.push(`sea floor ×${s.seaFloorExaggeration}`)
	return parts.join(" · ")
}

/** "Has this happened here before?" */
export function historySentences(h: QuakeCutoutHistory): string[] {
	const thisYear = new Date().getUTCFullYear()
	const parts: string[] = []
	parts.push(
		h.countNearby === 0
			? `No M${h.minMag}+ on record within ${h.radiusKm} km since ${h.sinceYear}`
			: `${h.countNearby} M${h.minMag}+ within ${h.radiusKm} km since ${h.sinceYear}`
	)
	if (h.largest) parts.push(`largest M ${h.largest.mag.toFixed(1)} in ${h.largest.year}`)
	if (h.lastM7Year !== undefined) {
		parts.push(h.lastM7Year >= thisYear - 1 ? "last M7+ within the past year" : `last M7+ in ${h.lastM7Year}`)
	} else if (h.lastM6Year !== undefined) {
		parts.push(`last M6+ in ${h.lastM6Year}`)
	}
	const lines = [parts.join(" · ")]
	const extras: string[] = []
	if (h.ghostCount > 0) extras.push(`${h.ghostCount} shown as ghosts in the block`)
	if (h.hiddenDeeper > 0) extras.push(`${h.hiddenDeeper} deeper than the box`)
	if (h.regionalB) extras.push(`regional b = ${h.regionalB.b.toFixed(2)} ± ${h.regionalB.stdErr.toFixed(2)}`)
	if (extras.length > 0) lines.push(extras.join(" · "))
	return lines
}

/** Fitted plane vs. the moment tensor */
export function geometrySentence(a: QuakeCutoutAnalysis): string | undefined {
	const p = a.planes
	if (!p) return undefined
	if (p.fit && !p.fit.dipResolved && !p.fit.trendResolved) {
		let line = `Aftershocks form a compact cloud with no clear trend (${p.fit.n} events, catalog depths mostly fixed)`
		if (p.nodalPlanes) line += ` · moment-tensor planes ${npText(p.nodalPlanes.np1)} and ${npText(p.nodalPlanes.np2)}`
		return line
	}
	if (p.fit) {
		let line = p.fit.dipResolved
			? `Aftershocks define a plane striking ${pad3(p.fit.strikeDeg)}°, dipping ${Math.round(p.fit.dipDeg)}° (${p.fit.n} events)`
			: `Aftershocks trend ${pad3(p.fit.strikeDeg)}° (${p.fit.n} events; catalog depths mostly fixed, so no dip)`
		if (p.nodalPlanes) {
			const np = p.matchedPlane === 1 ? p.nodalPlanes.np1 : p.matchedPlane === 2 ? p.nodalPlanes.np2 : undefined
			line += np
				? ` · ${p.fit.dipResolved ? "matches" : "aligns with"} moment-tensor plane ${p.matchedPlane} (${npText(np)})`
				: ` · neither moment-tensor plane within 25° (${npText(p.nodalPlanes.np1)}, ${npText(p.nodalPlanes.np2)})`
		}
		return line
	}
	if (p.nodalPlanes) {
		return `Moment-tensor nodal planes ${npText(p.nodalPlanes.np1)} and ${npText(p.nodalPlanes.np2)} · too few aftershocks to pick one`
	}
	return undefined
}

/** b-value, Båth and Omori, one line each */
export function statisticsSentences(a: QuakeCutoutAnalysis, regionalB?: { b: number; stdErr: number }): string[] {
	const lines: string[] = []
	if (a.bValue) {
		let line = `b = ${a.bValue.b.toFixed(2)} ± ${a.bValue.stdErr.toFixed(2)} (Mc ${a.bValue.mc.toFixed(1)}, n = ${a.bValue.n})`
		if (regionalB) line += ` · region since 1900: b = ${regionalB.b.toFixed(2)} ± ${regionalB.stdErr.toFixed(2)}`
		lines.push(line)
	}
	if (a.largestAftershock !== undefined) {
		const diff = a.largestAftershock - a.bathExpected
		const verdict = Math.abs(diff) <= 0.3 ? "as Båth's law expects" : diff > 0 ? "larger than Båth's law expects" : "smaller than Båth's law expects"
		lines.push(`Largest aftershock M ${a.largestAftershock.toFixed(1)} vs M ${a.bathExpected.toFixed(1)} expected · ${verdict}`)
	}
	if (a.omori && a.omori.firstDay > 0) {
		const ratio = a.omori.lastDay / a.omori.firstDay
		const recent = a.omori.lastDay === 0 ? "none in the past day" : `${a.omori.lastDay} in the past day`
		const verdict = ratio < 0.25 ? "decaying as Omori's law predicts" : ratio < 0.6 ? "slowing" : "still active"
		lines.push(`${a.omori.firstDay} aftershock${a.omori.firstDay === 1 ? "" : "s"} in the first day, ${recent} · ${verdict}`)
	}
	return lines
}

/** The USGS Operational Aftershock Forecast for the window that contains now */
export function forecastSentence(f: QuakeCutoutAnalysis["forecast"]): string | undefined {
	if (!f || f.windows.length === 0) return undefined
	const now = Date.now()
	if (f.expireTimeMs && now > f.expireTimeMs) return undefined
	// Prefer the week-long window when published; it is the one the USGS leads with
	const window = f.windows.find(w => /week/i.test(w.label)) ?? f.windows[0]
	const parts: string[] = []
	for (const bin of window.bins) {
		if (bin.magnitude < 5) continue
		parts.push(`M${bin.magnitude}+ ${formatProbability(bin.probability)}`)
	}
	if (parts.length === 0) return undefined
	const when = window.timeStartMs > now
		? `The ${window.label.toLowerCase()} from ${new Date(window.timeStartMs).toLocaleDateString()}`
		: `Next ${window.label.toLowerCase().replace(/^1 /, "")}`
	return `${when}: ${parts.join(" · ")}${f.reviewed ? "" : " (automatic)"}`
}

/** Slab2 interface relative to the hypocenter */
export function slabSentence(slab: QuakeCutoutSlab, mainshockDepthKm: number): string {
	const zone = cSlabZoneNames[slab.zone] ?? slab.zone.toUpperCase()
	if (slab.depthAtEpicenterKm === undefined) {
		return `${zone} subduction interface crosses the block (${Math.round(slab.minDepthKm)}–${Math.round(slab.maxDepthKm)} km deep) but not beneath the epicenter`
	}
	const parts = [`${zone} subduction interface ${Math.round(slab.depthAtEpicenterKm)} km beneath the epicenter`]
	if (slab.dipDeg !== undefined && slab.strikeDeg !== undefined) {
		parts.push(`dipping ${Math.round(slab.dipDeg)}° (strike ${pad3(slab.strikeDeg)}°)`)
	}
	const offset = slab.hypocenterOffsetKm ?? (mainshockDepthKm - slab.depthAtEpicenterKm)
	switch (slab.relation) {
		case "interface": parts.push(`hypocenter within ${Math.round(Math.abs(offset))} km of it — an interface event`); break
		case "overriding": parts.push(`hypocenter ${Math.round(-offset)} km above it, in the overriding plate`); break
		case "slab": parts.push(`hypocenter ${Math.round(offset)} km below it, inside the subducting slab`); break
	}
	if (!slab.drawn) parts.push("below the box floor, not drawn")
	return parts.join(" · ")
}

const cSlabZoneNames: Record<string, string> = {
	alu: "Alaska–Aleutian", cal: "Calabria", cam: "Central America", car: "Caribbean", cas: "Cascadia",
	cot: "Cotabato", hal: "Halmahera", hel: "Hellenic", him: "Himalaya", hin: "Hindu Kush", izu: "Izu–Bonin",
	ker: "Kermadec–Tonga", kur: "Kuril–Kamchatka–Japan", mak: "Makran", man: "Manila", mue: "Muertos",
	pam: "Pamir", phi: "Philippines", png: "New Guinea", puy: "Puysegur", ryu: "Ryukyu", sam: "South America",
	sco: "Scotia", sol: "Solomon", sul: "Sulawesi", sum: "Sumatra–Java", van: "Vanuatu"
}

const cMmiRoman = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"]

export function mmiRoman(mmi: number): string {
	return cMmiRoman[Math.min(12, Math.max(1, Math.round(mmi)))]
}

/** Estimated shaking at cities */
export function exposureSentences(e: QuakeCutoutExposure, usgsMaxMmi: number | null | undefined): string[] {
	const lines: string[] = []
	const pop = (level: number): number => e.populationAtOrAbove[level] ?? 0
	const fmt = (n: number): string => n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n)
	const parts: string[] = []
	if (pop(6) > 0) parts.push(`~${fmt(pop(6))} people in cities at MMI VI+ (strong shaking)`)
	else if (pop(5) > 0) parts.push(`~${fmt(pop(5))} people in cities at MMI V+ (moderate shaking)`)
	else parts.push(`~${fmt(pop(4))} people in cities at MMI IV+ (light shaking)`)
	if (pop(8) > 0) parts.push(`${fmt(pop(8))} at VIII+ (severe)`)
	parts.push(`${e.cityCount} ${e.cityCount === 1 ? "city" : "cities"} within ${Math.round(e.radiusKm)} km`)
	lines.push(parts.join(" · "))
	const top = e.cities.slice(0, 3).map(c => `${c.name} ${mmiRoman(c.mmi)} (${fmt(c.population)}, ${Math.round(c.distanceKm)} km)`)
	if (top.length > 0) lines.push(top.join(" · "))
	if (usgsMaxMmi != null) lines.push(`USGS ShakeMap maximum MMI ${mmiRoman(usgsMaxMmi)} · estimate: ${e.model}`)
	else lines.push(`Estimate: ${e.model}, distance to the rupture`)
	return lines
}

/** Tsunami travel-time overlay */
export function tsunamiSentence(t: { edgeArrivalMin: number; isochronMin: number }): string {
	const edge = t.edgeArrivalMin < 1.5 ? "about a minute" : `about ${Math.round(t.edgeArrivalMin)} min`
	return `Shallow-water speed √(g·h): the front reaches the block edge ${edge} after the mainshock · isochrons every ${t.isochronMin} min on the sea floor · scrub the clock to watch it travel (travel time only, not wave height)`
}

export function formatProbability(p: number): string {
	if (p >= 0.995) return ">99%"
	if (p < 0.01) return "<1%"
	return `${Math.round(p * 100)}%`
}

/**
 * Depth ramp shared with cutout_common.glsl (shallow warm → deep cool);
 * t in [0, 1] over the box depth. Returns a CSS color
 */
export function depthColor(t: number): string {
	const stops: Array<[number, number, number]> = [
		[1.00, 0.84, 0.42],
		[0.95, 0.50, 0.28],
		[0.84, 0.24, 0.44],
		[0.50, 0.26, 0.66],
		[0.20, 0.38, 0.86]
	]
	const x = Math.min(1, Math.max(0, t)) * 4
	const i = Math.min(3, Math.floor(x))
	const f = x - i
	const c = stops[i].map((v, k) => v + (stops[i + 1][k] - v) * f)
	return `rgb(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)})`
}
